import React from 'react';
import { Navigate } from 'react-router-dom';
import Login from '../pages/Login/Login';
import CompanyRegister from '../pages/CompanyRegister/CompanyRegister';
import Unauthorized from '../pages/Unauthorized/Unauthorized';
import Dashboard from '../pages/Dashboard/Dashboard';
import Internships from '../pages/Internships/Internships';
import Companies from '../pages/Companies/Companies';
import JobPosts from '../pages/JobPosts/JobPosts';
import MyApplications from '../pages/MyApplications/MyApplications';
import Applications from '../pages/Applications/Applications';
import Profile from '../pages/Profile/Profile';
import SuggestedCompanies from '../pages/SuggestedCompanies/SuggestedCompanies';
import Majors from '../pages/Majors/Majors';
import MajorSelection from '../pages/MajorSelection/MajorSelection';
import WorkshopHub from '../pages/WorkshopHub/WorkshopHub';
import AssessmentCenter from '../pages/AssessmentCenter/AssessmentCenter';
import Certificates from '../pages/Certificates/Certificates';
import Reports from '../pages/Reports';
import FacultyEvaluations from '../pages/FacultyEvaluations';
import LandingPage from '../pages/Landing/LandingPage';
import { useAuth } from '../contexts/AuthContext';

// A wrapper component to protect routes requiring authentication
export const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { user } = useAuth();

  // If user is not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" />;
  }

  // If requiredRoles array is empty, allow access to any authenticated user
  if (requiredRoles.length === 0) {
    return children;
  }

  // If user's role is in the requiredRoles array, allow access
  if (requiredRoles.includes(user.role)) {
    return children;
  }

  // Otherwise, redirect to unauthorized page
  return <Navigate to="/unauthorized" />;
};

// Define the application routes
export const appRoutes = [
  {
    path: '/login',
    element: ({ user }) => (user ? <Navigate to="/dashboard" /> : <Login />)
  },
  {
    path: '/company-register',
    element: <CompanyRegister />
  },
  {
    path: '/unauthorized',
    element: <Unauthorized />
  },
  {
    path: '/dashboard',
    element: <Dashboard />,
    protected: true
  },
  {
    path: '/internships',
    element: <Internships />,
    protected: true
  },
  {
    path: '/companies',
    element: <Companies />,
    protected: true,
    requiredRoles: ['scadOffice']
  },
  {
    path: '/job-posts',
    element: <JobPosts />,
    protected: true,
    requiredRoles: ['company']
  },
  {
    path: '/my-applications',
    element: <MyApplications />,
    protected: true,
    requiredRoles: ['student', 'proStudent']
  },
  {
    path: '/applications',
    element: <Applications />,
    protected: true,
    requiredRoles: ['company']
  },
  {
    path: '/profile',
    element: <Profile />,
    protected: true,
    requiredRoles: ['student', 'proStudent']
  },
  {
    path: '/reports',
    element: <Reports />,
    protected: true,
    requiredRoles: ['student', 'proStudent']
  },
  {
    path: '/evaluations',
    element: <FacultyEvaluations />,
    protected: true,
    requiredRoles: ['faculty']
  },
  {
    path: '/suggested-companies',
    element: <SuggestedCompanies />,
    protected: true,
    requiredRoles: ['student', 'proStudent']
  },
  {
    path: '/majors',
    element: <Majors />,
    protected: true,
    requiredRoles: ['student', 'proStudent']
  },
  {
    path: '/major-selection',
    element: <MajorSelection />,
    protected: true,
    requiredRoles: ['student', 'proStudent']
  },
  {
    path: '/workshops',
    element: <WorkshopHub />,
    protected: true,
    requiredRoles: ['proStudent']
  },
  {
    path: '/assessments',
    element: <AssessmentCenter />,
    protected: true,
    requiredRoles: ['proStudent']
  },
  {
    path: '/certificates',
    element: <Certificates />,
    protected: true,
    requiredRoles: ['proStudent']
  },
  {
    path: '/',
    element: <LandingPage />
  },
  {
    path: '*',
    element: <Navigate to="/" />
  }
];

// Helper function to generate route elements with protection
export const getRouteElement = (route, user) => {
  if (route.element && typeof route.element === 'function') {
    return route.element({ user });
  }

  if (route.protected) {
    return (
      <ProtectedRoute requiredRoles={route.requiredRoles || []}>
        {route.element}
      </ProtectedRoute>
    );
  }

  return route.element;
};

export default appRoutes;